import { Button, Table, Tooltip } from "antd";
import ChantPopConfirm from "../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fbDeleteChant,
  fbGetParentGroupChants,
  fbUpdateChant,
} from "../../../services/firebaseService/endPoints/admin/ourChants";
import {
  GUIDE_SETUP,
  TOPIC_ADD_CHANTS,
} from "../../../constants/gettingStarted";
import { ArchiveIcon, EditIcon, UnarchiveIcon } from "../../../utils/icons";
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../services/firebaseService/connection";
import { postUpdateCDNUtil } from "../../../helperFunctions/util";
import logger from "../../../utils/logger";
import { debounce } from "../../../utils/helper";
import { useNavigate } from "react-router-dom";
import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import { fetchGroupDetails } from "../../../services/firebaseService/endPoints/group";

const RowContext = React.createContext({});

const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style = {
    ...props.style,
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  const childrenArray = React.Children.toArray(props.children);
  if (childrenArray.length > 0) {
    childrenArray[0] = React.cloneElement(childrenArray[0], {});
  }

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes}>
        {childrenArray}
      </tr>
    </RowContext.Provider>
  );
};

const ClubChants = ({ archive, setChantsCount }) => {
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const groupData = useAdminDataStore(selectCurrentGroupData);

  const updateChantsSortOrder = useCallback(
    async (updatedSortOrderIds) => {
      try {
        if (groupData?.id) {
          await updateDoc(doc(db, "group", groupData?.id), {
            sortOrderChantIds: updatedSortOrderIds,
          });

          postUpdateCDNUtil(groupData?.id);
        }
        // dispatch(startUpdateUser());
      } catch (error) {
        logger.error(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupData?.id]
  );

  const debouncedUpdateChantsSortOrder = useMemo(
    () => debounce((ids) => updateChantsSortOrder(ids), 3000),
    [updateChantsSortOrder]
  );

  const DragHandle = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        style={{ cursor: "move" }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const columns = [
    { key: "sort", align: "center", width: 20, render: () => <DragHandle /> },
    {
      title: "Actions",
      className: "header-white",
      width: 50,
      align: "center",
      render: (data) => {
        return (
          <div className="flex-center" style={{ gap: "4px" }}>
            <>
              <Tooltip title="edit">
                <img
                  onClick={() => handleEditChant(data)}
                  src={EditIcon}
                  alt="edit-member"
                  style={{ width: 20, height: 20 }}
                />
              </Tooltip>
              &nbsp;&nbsp;
              <ChantPopConfirm
                title={"Delete chant?"}
                onConfirm={() => {
                  handleDeleteChant(data);
                }}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="delete">
                  <DeleteOutlined style={{ fontSize: 18 }} />
                </Tooltip>
              </ChantPopConfirm>
              &nbsp;&nbsp;
              <ChantPopConfirm
                title={`${data.isPublished ? "Archive chant?" : "Publish chant?"}`}
                onConfirm={() => {
                  handleChangePublish(data);
                }}
                onCancel={() => {}}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title={`${data.isPublished ? "archive" : "publish"}`}>
                  <i className="fa cursor-pointer" aria-hidden="true">
                    {data.isPublished ? (
                      <img
                        src={ArchiveIcon}
                        style={{ width: 25, height: 25 }}
                        alt="Archive"
                      />
                    ) : (
                      <img
                        src={UnarchiveIcon}
                        style={{ width: 25, height: 25 }}
                        alt="Publish"
                      />
                    )}
                  </i>
                </Tooltip>
              </ChantPopConfirm>
            </>
          </div>
        );
      },
    },
    {
      title: "Name",
      width: 320,
      key: "title",
      className: "header-white",
      dataIndex: "title",
    },
    {
      title: "Video",
      width: 250,
      key: "videoLink",
      className: "header-white",
      dataIndex: "videoLink",
      render: (videoLink) => {
        let link = videoLink;
        if (link) {
          if (!`${link}`.includes("http")) {
            link = `https://${link}`;
          }
          return (
            <a href={link} target="_blank" rel="noopener noreferrer">
              Link
            </a>
          );
        }
        return <></>;
      },
    },
  ];

  const getChants = async (groupId, archive) => {
    try {
      setIsLoading(true);

      const chantsList = await fbGetParentGroupChants(groupId, !archive);

      let tableData = chantsList || [];

      if (tableData?.length > 0) {
        useAdminDataStore.getState().setGettingStartedItemStatus(
          groupId,
          GUIDE_SETUP,
          TOPIC_ADD_CHANTS,
          true
        );
      }

      const parentGroupData = await fetchGroupDetails(groupId);

      let sortOrderChantIds = parentGroupData?.sortOrderChantIds || [];
      tableData = tableData
        .map((obj) => {
          return {
            ...obj,
            sortIndex:
              sortOrderChantIds.indexOf(obj.id) !== -1
                ? sortOrderChantIds.indexOf(obj.id) + 1
                : sortOrderChantIds.length + 1,
          };
        })
        .sort((a, b) => a.sortIndex - b.sortIndex);

      setTableData(tableData);
      setChantsCount(tableData.length);
    } catch {
      logger.error("Failed to fetch club chants.");
    } finally {
      setIsLoading(false);
    }
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setTableData((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.id === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.id === over?.id
        );

        const updatedSortOrder = arrayMove(prevState, activeIndex, overIndex);
        const updatedSortOrderIds = updatedSortOrder.map((item) => item.id);

        debouncedUpdateChantsSortOrder(updatedSortOrderIds);

        return updatedSortOrder;
      });
    }
  };

  const removeChantFromList = (id) => {
    setTableData((prev) => prev.filter((data) => data.id !== id));
    setIsLoading(false);
  };

  const handleChangePublish = (data) => {
    setIsLoading(true);

    fbUpdateChant(groupData?.id, data.id, {
      isPublished: !data.isPublished,
    }).then(() => {
      removeChantFromList(data.id);
    });
  };

  const handleDeleteChant = (data) => {
    setIsLoading(true);

    fbDeleteChant(groupData?.id, data.id).then(() => {
      removeChantFromList(data.id);
    });
  };

  const handleEditChant = (data) => {
    navigate(`/clubadmin/chants/edit/${groupData?.id}/${data.id}`);
  };

  useEffect(() => {
    if (groupData?.id) {
      getChants(groupData?.id, archive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id, archive]);

  return (
    <div className="chants-container">
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={tableData.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            bordered
            columns={columns}
            loading={isLoading}
            components={{
              body: {
                row: Row,
              },
            }}
            dataSource={tableData}
            pagination={
              tableData?.length > 50 && {
                position: ["bottomLeft"],
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
              }
            }
            rowKey="id"
            scroll={{ x: "max-content" }}
          />
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ClubChants;
